import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Network } from 'lucide-react';

/**
 * Heatmap regime × strategia.
 *
 * Props:
 *  - byRegime: { [regime]: [{strategy, sharpe_cond}] } da quantKgSummary().top_strategies_per_regime
 *  - onCellClick?: (strategy, regime) => void
 */
const RegimeHeatmap = ({ byRegime, onCellClick }) => {
  const { regimes, strategies, matrix, minV, maxV } = useMemo(() => {
    if (!byRegime || !Object.keys(byRegime).length) {
      return { regimes: [], strategies: [], matrix: {}, minV: 0, maxV: 0 };
    }
    const regimeNames = Object.keys(byRegime);
    const stratSet = new Set();
    Object.values(byRegime).forEach(arr => arr.forEach(e => stratSet.add(e.strategy)));
    const strategyNames = Array.from(stratSet).sort();

    const m = {};
    let mn = Infinity, mx = -Infinity;
    for (const r of regimeNames) {
      m[r] = {};
      for (const e of byRegime[r]) {
        m[r][e.strategy] = e.sharpe_cond;
        if (e.sharpe_cond < mn) mn = e.sharpe_cond;
        if (e.sharpe_cond > mx) mx = e.sharpe_cond;
      }
    }
    return { regimes: regimeNames, strategies: strategyNames, matrix: m, minV: mn, maxV: mx };
  }, [byRegime]);

  if (!regimes.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          KG non popolato. Esegui Quant Lab Fase 3 per generare archi performs_in.
        </CardContent>
      </Card>
    );
  }

  // Colore: rosso forte → bianco → verde forte, scala lineare su [minV, maxV]
  const cellColor = (v) => {
    if (v === undefined || v === null || Number.isNaN(v)) return 'transparent';
    if (v >= 0) {
      const t = maxV > 0 ? Math.min(1, v / maxV) : 0;
      // verde
      const a = 0.15 + 0.55 * t;
      return `rgba(16, 185, 129, ${a.toFixed(2)})`;
    } else {
      const t = minV < 0 ? Math.min(1, v / minV) : 0;
      const a = 0.15 + 0.55 * t;
      return `rgba(239, 68, 68, ${a.toFixed(2)})`;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" /> Heatmap regime × strategia
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <p className="text-xs text-muted-foreground mb-2">
          Sharpe condizionato. Verde = positivo, rosso = negativo, vuoto = strategia non testata in quel regime.
        </p>
        <table className="text-xs border-collapse">
          <thead>
            <tr>
              <th className="px-2 py-1 text-left font-medium text-muted-foreground">Regime ↓ / Strategia →</th>
              {strategies.map(s => (
                <th key={s} className="px-2 py-1 text-center font-medium text-muted-foreground rotate-0 min-w-[110px]">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {regimes.map(r => (
              <tr key={r}>
                <td className="px-2 py-1 font-medium capitalize whitespace-nowrap">{r}</td>
                {strategies.map(s => {
                  const v = matrix[r]?.[s];
                  const hasVal = v !== undefined && v !== null && !Number.isNaN(v);
                  return (
                    <td
                      key={s}
                      onClick={() => hasVal && onCellClick?.(s, r)}
                      className={`px-2 py-1.5 text-center border tabular-nums ${
                        hasVal ? 'cursor-pointer hover:ring-2 hover:ring-primary/40 transition' : 'text-muted-foreground/40'
                      }`}
                      style={{ backgroundColor: cellColor(v) }}
                      title={hasVal ? `${s} in ${r}: Sharpe ${v.toFixed(2)}` : 'non testata'}
                    >
                      {hasVal ? (v >= 0 ? '+' : '') + v.toFixed(2) : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export default RegimeHeatmap;
